import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Button from '../../components/Button';
import Table, { Column } from '../../components/Table';
import Modal from '../../components/Modal';
import Input from '../../components/Input';
import Select from '../../components/Select';

interface PayDayGroup {
  id: string;
  code: string;
  name: string;
  year: number;
  is_ph_working_day: boolean;
  salary_pay_mode: string;
}

export default function PayDayGroups() {
  const [groups, setGroups] = useState<PayDayGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', year: new Date().getFullYear(), isPhWorkingDay: false, salaryPayMode: 'MONTHLY' });
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get('/payroll/pay-day-groups').then((res) => setGroups(res.data.payDayGroups)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    setError('');
    try {
      const res = await api.post('/payroll/pay-day-groups', form);
      setShowForm(false);
      navigate(`/payroll/pay-day-groups/${res.data.payDayGroup.id}`);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to create Pay Day Group');
    }
  };

  const columns: Column<PayDayGroup>[] = [
    { key: 'code', header: 'Code', sortable: true },
    { key: 'name', header: 'Name', sortable: true },
    { key: 'year', header: 'Year', sortable: true },
    { key: 'salary_pay_mode', header: 'Salary Pay Mode' },
    { key: 'is_ph_working_day', header: 'PH Working Day?', render: (r) => (r.is_ph_working_day ? 'Yes' : 'No') },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <Button variant="ghost" onClick={() => navigate(`/payroll/pay-day-groups/${r.id}`)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Pay Day Group</h1>
        <Button onClick={() => setShowForm(true)}>+ New Pay Day Group</Button>
      </div>
      <Table columns={columns} rows={groups} loading={loading} />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="New Pay Day Group — Step 1">
        <div className="space-y-3">
          <Input label="Code (max 6)" maxLength={6} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Name (max 50)" maxLength={50} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Year" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: Number(e.target.value) })} />
          <Select
            label="Salary Pay Mode"
            value={form.salaryPayMode}
            onChange={(e) => setForm({ ...form, salaryPayMode: e.target.value })}
            options={[
              { value: 'MONTHLY', label: 'Monthly' },
              { value: 'BI_MONTHLY', label: 'Bi-Monthly' },
            ]}
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isPhWorkingDay} onChange={(e) => setForm({ ...form, isPhWorkingDay: e.target.checked })} />
            Is PH Working Day?
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={create}>Create & Continue</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
